import { PatientConversation, buildConversation } from "@/features/patient/medications/components/PatientConversation";
import type { ClinicianReviewItem } from "@/portals/clinician/api/reviewQueueApi";
import { actionLabel } from "./DecisionPanel";
import { formatBand, formatScore } from "./reviewBadges";

type Correction = NonNullable<NonNullable<ClinicianReviewItem["clinicianActions"]>[number]["correctedValue"]>;

/** The corrected fields only — the rxnorm sub-object is the backend's lookup, not a field. */
function correctedEntries(value: Correction) {
  return Object.entries(value).filter(([k]) => k !== "rxnorm");
}

/**
 * The Patient Conversation on the clinician's review screen: every question
 * asked, the patient's answers and replies, and the decision (with what it
 * corrected), as one thread.
 */
export function ReviewHistory({ item, isPair }: { item: ClinicianReviewItem; isPair: boolean }) {
  const actions = item.clinicianActions ?? [];
  const messages = buildConversation({
    questions: actions.filter((a) => a.action === "REQUEST_MORE_INFO").map((a) => ({ text: a.reason, askedAt: a.createdAt })),
    responses: item.patientResponses,
    decisions: actions
      .filter((a) => a.action !== "REQUEST_MORE_INFO")
      .map((a) => {
        const corrected = a.correctedValue ? correctedEntries(a.correctedValue) : [];
        const rx = a.correctedValue?.rxnorm;
        return {
          at: a.createdAt,
          label: actionLabel(a.action, isPair),
          reason: a.reason,
          details: (corrected.length > 0 || rx?.rxcui) && (
            <>
              {corrected.length > 0 && (
                <p className="!mb-0 mt-1 text-xs text-[#027A48]">
                  Corrected: {corrected.map(([k, v]) => `${k.replace(/_/g, " ")} → ${v}`).join(", ")}
                </p>
              )}
              {rx?.rxcui && (
                <p className="!mb-0 mt-0.5 text-xs text-[#4B5563]" data-testid="history-rxnorm">
                  RxNorm match: {rx.rxcui} ({formatBand(rx.confidenceBand)}, {formatScore(rx.confidenceScore)})
                </p>
              )}
            </>
          ),
        };
      }),
  });
  return (
    <PatientConversation
      messages={messages}
      viewer="clinician"
      waitingFor={item.itemStatus === "NEEDS_MORE_INFO" ? "patient" : null}
      emptyText="No conversation yet. Use “Ask the patient a question” to start one."
    />
  );
}
